import { useCallback, useEffect, useState } from "react";
import {
  api,
  ApiError,
  type ClasseDGA,
  type EstadoDGA,
  type EvolucaoGasDGA,
  type GasTipicoDGA,
  type RazaoDGA,
  type RespostaDGA,
} from "../api";
import { IconAlert, IconCheck, IconFlask, IconScale, IconClock } from "../icons";

/**
 * DIAGNÓSTICO DE DGA — a tela dos três caminhos.
 *
 *   NORMAL    o analista concluiu que os valores são de operação normal
 *   POR IA    o sistema calcula as três razões, acha a classe e redige
 *   ALTERAR   o analista escolhe a classe (DP, D1, D2, T1, T2, T3)
 *
 * As TRÊS RAZÕES aparecem antes dos botões, e de propósito: a conta é o que se
 * confere, e ela precisa estar visível ANTES da decisão — não como
 * justificativa depois dela.
 *
 * Antes delas, os VALORES TÍPICOS (NBR 7274, 9.1): é a primeira pergunta da
 * norma. Todos os gases dentro do típico = condição normal, e o botão "Gerar"
 * registra isso sozinho; as relações só entram quando algum gás passa do típico.
 *
 * A EVOLUÇÃO fica embaixo porque responde outra pergunta. Concentração diz
 * onde o gás está; taxa mensal diz para onde ele vai. Um transformador com
 * 120 ppm de H₂ estável há dois anos e outro que saiu de 12 para 120 em seis
 * meses têm o mesmo número e histórias opostas.
 *
 * O ALERTA DE ACETILENO é uma faixa própria, fora do diagnóstico: ele vale
 * mesmo quando a classe é de sobreaquecimento, porque acetileno praticamente
 * não se forma por calor — é o gás que separa "esquentou" de "arcou".
 */

interface Props {
  analiseId: number;
  /** Só quem tem a permissão de diagnóstico escreve. */
  podeEditar: boolean;
  /** Avisa a tela de cima que o parecer da seção mudou (para recarregar). */
  onGravado: (r: RespostaDGA) => void;
  /**
   * Muda quando o que decide o valor típico muda (o equipamento vinculado, na
   * tela de cima) — o painel recarrega, senão mostraria a comparação de antes.
   */
  recarregarQuando?: unknown;
}

/** A situação de cada gás contra o valor típico, em palavra de bancada. */
const ROTULO_SITUACAO: Record<GasTipicoDGA["situacao"], string> = {
  abaixo: "dentro do típico",
  acima: "acima do típico",
  "sem-valor": "sem valor",
  "nao-numerico": "não é número",
  ambiguo: "confira o valor",
  "falta-dado": "falta dado",
  "fora-do-escopo": "fora da tabela",
};

/**
 * Por que "Gerar" não gravou nada — com o que a pessoa pode fazer.
 *
 * O servidor manda o motivo das relações em `mensagem` (nenhuma condição, ou
 * duas ao mesmo tempo) e, quando o Normal foi segurado, o `bloqueio`. A parte
 * dos valores típicos só entra quando ela explica alguma coisa.
 */
function motivoSemDiagnostico(r: RespostaDGA): string {
  const partes: string[] = [r.bloqueio ?? `Nenhum diagnóstico foi gravado. ${r.mensagem}`];
  if (!r.bloqueio && r.tipicos && !r.tipicos.todosAbaixo) partes.push(r.tipicos.mensagem);
  partes.push(
    "Se os valores são de operação normal, clique em “Normal”; se não, use “Alterar/selecionar " +
      "diagnóstico” e escolha a condição.",
  );
  if (r.aviso) partes.push(r.aviso);
  return partes.join(" ");
}

const nada = "—";

const ppm = (v: number | null | undefined) => (v === null || v === undefined ? nada : `${v} ppm`);

const sinal = (v: number | null) =>
  v === null ? nada : `${v > 0 ? "+" : ""}${v.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}`;

const dataBR = (d: string | null) => {
  if (!d) return nada;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

/** Cor da tendência: só o que sobe é preocupante num gás dissolvido. */
function tomDaTendencia(t: EvolucaoGasDGA["tendencia"]): string {
  if (t === "crescente") return "danger";
  if (t === "decrescente") return "ok";
  if (t === "estável") return "neutral";
  return "neutral";
}

export default function DiagnosticoDGA({ analiseId, podeEditar, onGravado, recarregarQuando }: Props) {
  const [estado, setEstado] = useState<EstadoDGA | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<"normal" | "ia" | "manual" | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  /**
   * "Gerar por IA" rodou, mas as três relações não caíram em nenhuma condição
   * da tabela. NÃO é sucesso: o parecer fica em branco. Antes isto saía na
   * linha verde de "ok" e a pessoa via só a caixa de texto vazia — parecia que
   * a IA tinha travado. Fica separado para sair como aviso, com o próximo passo.
   */
  const [semEnquadramento, setSemEnquadramento] = useState<string | null>(null);

  // Escolha manual (§7): a classe e a justificativa opcional.
  const [abrirManual, setAbrirManual] = useState(false);
  const [classeEscolhida, setClasseEscolhida] = useState("");
  const [justificativa, setJustificativa] = useState("");


  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setEstado(await api.analises.dga(analiseId));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível carregar o diagnóstico de DGA.");
    } finally {
      setCarregando(false);
    }
  }, [analiseId]);

  useEffect(() => {
    void carregar();
  }, [carregar, recarregarQuando]);

  async function executar(qual: "normal" | "ia" | "manual") {
    if (qual === "manual" && !classeEscolhida) {
      setErro("Escolha a classe do diagnóstico.");
      return;
    }
    setOcupado(qual);
    setErro(null);
    setMensagem(null);
    setSemEnquadramento(null);
    try {
      const r =
        qual === "normal"
          ? await api.analises.dgaNormal(analiseId)
          : qual === "ia"
            ? await api.analises.dgaGerar(analiseId)
            : await api.analises.dgaManual(analiseId, classeEscolhida, justificativa);
      if (qual === "ia" && r.enquadrado === false) {
        setSemEnquadramento(motivoSemDiagnostico(r));
      } else {
        setMensagem(r.mensagem + (r.aviso ? ` ${r.aviso}` : ""));
      }
      setAbrirManual(false);
      setJustificativa("");
      await carregar();
      onGravado(r);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível gravar o diagnóstico.");
    } finally {
      setOcupado(null);
    }
  }

  if (carregando) return <div className="dga-caixa">Carregando o diagnóstico de gases...</div>;
  if (!estado) {
    return (
      <div className="dga-caixa dga-erro">
        {erro ?? "Não foi possível carregar o diagnóstico de DGA."}
      </div>
    );
  }

  const { razoes, evolucao, classes, salvo, tipicos, automatico } = estado;
  const alerta = evolucao.alertaAcetileno;
  const travado = !podeEditar || estado.parecerFechado || estado.analise.despachada;

  /** O que "Gerar" faria agora, numa frase — lida antes do clique. */
  const previsao = !automatico
    ? null
    : automatico.motivo === "tipicos"
      ? "Com estes valores, “Gerar” registra condição normal de operação (todos os gases dentro do típico)."
      : automatico.motivo === "razoes" && automatico.classe
        ? `Com estes valores, “Gerar” registra ${automatico.classe.caso} — ${automatico.classe.rotulo} (pelas relações entre gases).`
        : "Com estes valores o sistema não conclui sozinho — use “Normal” ou “Alterar/selecionar diagnóstico”.";

  return (
    <div className="dga">
      {/* ---------------- Concentrações medidas ---------------- */}
      <div className="dga-caixa">
        <h4 className="dga-titulo">
          <IconFlask size={16} /> Concentrações medidas
        </h4>
        <div className="dga-gases">
          {[
            ["h2", "H₂"],
            ["ch4", "CH₄"],
            ["c2h6", "C₂H₆"],
            ["c2h4", "C₂H₄"],
            ["c2h2", "C₂H₂"],
          ].map(([chave, rotulo]) => (
            <div key={chave} className="dga-gas">
              <span className="dga-gas-rotulo">{rotulo}</span>
              <strong>{ppm(estado.concentracoes[chave!])}</strong>
            </div>
          ))}
        </div>
      </div>

      {/* ---------------- Valores típicos (9.1) — a primeira pergunta ---------------- */}
      {tipicos && (
        <div className="dga-caixa">
          <h4 className="dga-titulo">
            <IconCheck size={16} /> Valores típicos (NBR 7274 · Tabela A.2)
          </h4>
          {tipicos.gases.length > 0 && (
            <div className="dga-rolagem">
              <table className="dga-tabela">
                <thead>
                  <tr>
                    <th>Gás</th>
                    <th>Medido</th>
                    <th>Típico</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {tipicos.gases.map((g) => (
                    <tr key={g.chave}>
                      <td>{g.rotulo}</td>
                      <td>{g.valor !== null ? `${g.valor} ppm` : (g.valorInformado ?? nada)}</td>
                      <td>{g.tipico ?? nada}</td>
                      <td>
                        <span className={`pill pill-${g.situacao === "abaixo" ? "ok" : "neutral"}`}>
                          {ROTULO_SITUACAO[g.situacao]}
                        </span>
                        {/* O porquê fica escrito (e não num "title"): no celular não há mouse. */}
                        {g.situacao !== "abaixo" && g.situacao !== "acima" && (
                          <div className="dga-sub">{g.explicacao}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className={tipicos.todosAbaixo ? "dga-ok" : "dga-nota"}>{tipicos.mensagem}</p>
          {tipicos.faltando.map((f) => (
            <p key={f.eixo} className="dga-aviso">
              {f.rotulo}: {f.comoResolver}
            </p>
          ))}
        </div>
      )}

      {/* ---------------- As três razões ---------------- */}
      <div className="dga-caixa">
        <h4 className="dga-titulo">
          <IconScale size={16} /> Relações características (NBR 7274)
        </h4>
        <table className="dga-tabela">
          <thead>
            <tr>
              <th>Relação</th>
              <th>Conta</th>
              <th>Valor</th>
            </tr>
          </thead>
          <tbody>
            {razoes.razoes.map((r: RazaoDGA) => (
              <tr key={r.chave}>
                <td>
                  <b>{r.chave.toUpperCase()}</b> = {r.formula}
                </td>
                <td>
                  {r.numerador.valor ?? nada} / {r.denominador.valor ?? nada}
                </td>
                <td>
                  {r.valor !== null ? (
                    <b>{r.valor}</b>
                  ) : (
                    <span className="dga-nao-aplicavel">não aplicável — {r.motivo}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* A prova do enquadramento: condição por condição, cada relação dentro
            ou fora. Só enquadra a linha em que as TRÊS estão dentro ao mesmo
            tempo — "duas de três" aparece aqui e não vira diagnóstico. */}
        {razoes.avaliacao && razoes.avaliacao.length > 0 && (
          <table className="dga-tabela dga-enquadramento">
            <thead>
              <tr>
                <th>Condição</th>
                <th>R1</th>
                <th>R2</th>
                <th>R3</th>
                <th>Enquadra?</th>
              </tr>
            </thead>
            <tbody>
              {razoes.avaliacao.map((a) => (
                <tr key={a.caso} style={a.enquadra ? { fontWeight: 600 } : undefined}>
                  <td>
                    {a.caso} — {a.rotulo}
                  </td>
                  {[a.r1, a.r2, a.r3].map((v, i) => (
                    <td key={i} title={v === null ? "Não significativo para esta condição" : undefined}>
                      {v === null ? "NS" : v ? "dentro" : "fora"}
                    </td>
                  ))}
                  <td>{a.enquadra ? "sim" : "não"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="dga-nota">{razoes.mensagem}</p>
        {razoes.caso && razoes.caso.reamostragem_meses != null && (
          <p className="dga-nota">
            Prazo cadastrado para {razoes.caso.caso}: <b>{razoes.caso.reamostragem_meses}{" "}
            {Number(razoes.caso.reamostragem_meses) === 1 ? "mês" : "meses"}</b> até a próxima
            reamostragem.
          </p>
        )}
        {estado.avisoCadastro && <p className="dga-aviso">{estado.avisoCadastro}</p>}
      </div>

      {/* ---------------- Alerta de acetileno (independente) ---------------- */}
      {alerta?.ativo && (
        <div className="dga-caixa dga-alerta">
          <h4 className="dga-titulo">
            <IconAlert size={16} /> Alerta de acetileno
          </h4>
          <p className="dga-alerta-texto">{alerta.texto}</p>
          <p className="dga-nota">{alerta.motivo}</p>
          <p className="dga-nota">
            Este alerta é independente do diagnóstico: ele vale mesmo quando a classe indicar
            sobreaquecimento, porque o acetileno praticamente não se forma por calor.
          </p>
        </div>
      )}

      {/* ---------------- Os três caminhos ---------------- */}
      <div className="dga-caixa">
        <h4 className="dga-titulo">Diagnóstico</h4>

        {salvo?.origem && (
          <p className="dga-salvo">
            Registrado como{" "}
            <b>
              {salvo.origem === "normal"
                ? "condição normal"
                : salvo.motivo === "tipicos"
                  ? "condição normal (gases dentro do típico)"
                  : salvo.classe?.caso
                    ? `${salvo.classe.caso} — ${salvo.classe.rotulo}`
                    : "sem classe"}
            </b>{" "}
            (
            {salvo.origem === "ia"
              ? "gerado pelo sistema"
              : salvo.origem === "manual"
                ? "escolhido pelo analista"
                : "condição normal"}
            ){salvo.por ? ` por ${salvo.por}` : ""}.
            {/* O que o cálculo tinha dito continua à vista mesmo depois da
                troca manual — é o que responde "o sistema dizia outra coisa?" */}
            {salvo.origem === "manual" && salvo.classeCalculada?.caso && (
              <>
                {" "}
                O cálculo das razões havia indicado <b>{salvo.classeCalculada.caso}</b>.
              </>
            )}
          </p>
        )}

        {estado.parecerFechado && (
          <p className="dga-aviso">
            O parecer de Gases Dissolvidos desta amostra está fechado. Reabra o parecer para
            trocar o diagnóstico.
          </p>
        )}

        {previsao && !travado && <p className="dga-nota dga-previsao">{previsao}</p>}

        <div className="dga-botoes">
          <button
            type="button"
            className="btn-secondary"
            disabled={travado || ocupado !== null}
            onClick={() => void executar("normal")}
            title="Os valores representam condição normal de operação"
          >
            <IconCheck size={15} /> {ocupado === "normal" ? "Registrando..." : "Normal"}
          </button>

          <button
            type="button"
            className="btn-primary"
            disabled={travado || ocupado !== null}
            onClick={() => void executar("ia")}
          >
            <IconFlask size={15} />{" "}
            {ocupado === "ia" ? "Gerando..." : "Gerar diagnóstico por IA"}
          </button>

          <button
            type="button"
            className="btn-secondary"
            disabled={travado || ocupado !== null}
            onClick={() => setAbrirManual((v) => !v)}
          >
            <IconScale size={15} /> Alterar/selecionar diagnóstico
          </button>
        </div>

        <p className="dga-nota">
          “Gerar” segue a ordem da norma: com todos os gases dentro do típico, registra condição
          normal (frase, prazo e status Normal); senão, a frase da condição enquadrada pelas relações
          (ex.: “Os resultados indicam descargas parciais.”) e o prazo cadastrado. As contas acima
          ficam guardadas no histórico da análise — não vão para o laudo.
        </p>

        {abrirManual && (
          <div className="dga-manual">
            <p className="dga-nota">
              Use quando a interpretação do especialista não for a que as relações produzem — por
              exemplo, óleo que já tinha etano antes e passou a apresentar acetileno sem o etano
              crescer junto. O resultado calculado continua gravado no histórico da análise.
            </p>
            <div className="dga-manual-linha">
              <label className="dga-campo">
                <span>Classe</span>
                <select
                  value={classeEscolhida}
                  onChange={(e) => setClasseEscolhida(e.target.value)}
                  disabled={travado}
                >
                  <option value="">Selecione...</option>
                  {classes.map((c: ClasseDGA) => (
                    <option key={c.id} value={c.caso}>
                      {c.caso} — {c.rotulo}
                    </option>
                  ))}
                </select>
              </label>
              <label className="dga-campo dga-campo-largo">
                <span>Justificativa (opcional, sai no laudo)</span>
                <input
                  type="text"
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  placeholder="ex.: etano pré-existente; acetileno recente sem crescimento proporcional"
                  disabled={travado}
                />
              </label>
              <button
                type="button"
                className="btn-primary"
                disabled={travado || ocupado !== null || !classeEscolhida}
                onClick={() => void executar("manual")}
              >
                {ocupado === "manual" ? "Aplicando..." : "Aplicar classe"}
              </button>
            </div>
            {classes.length === 0 && (
              <p className="dga-aviso">
                Nenhuma classe está ativa na aba "Razões NBR 7274" — cadastre e confira as linhas
                para poder escolher.
              </p>
            )}
          </div>
        )}

        {mensagem && <p className="dga-ok">{mensagem}</p>}
        {semEnquadramento && <p className="dga-aviso">{semEnquadramento}</p>}
        {erro && <p className="dga-erro-linha">{erro}</p>}
      </div>

      {/* ---------------- Evolução dos gases ---------------- */}
      <div className="dga-caixa">
        <h4 className="dga-titulo">
          <IconClock size={16} /> Taxa de evolução
        </h4>

        {!evolucao.temHistorico ? (
          <p className="dga-nota">{evolucao.mensagem}</p>
        ) : (
          <>
            <p className="dga-nota">{evolucao.mensagem}</p>
            {evolucao.correlacao === "cliente-equipamento" && (
              <p className="dga-aviso">
                Histórico correlacionado por cliente e descrição do equipamento. Vincule a análise
                a um equipamento cadastrado para a série ficar presa ao número de série.
              </p>
            )}
            <div className="dga-rolagem">
              <table className="dga-tabela">
                <thead>
                  <tr>
                    <th>Gás</th>
                    <th>Anterior</th>
                    <th>Atual</th>
                    <th>Δ ppm</th>
                    <th>Δ % total</th>
                    <th>Meses</th>
                    <th>Taxa mensal</th>
                    <th>Tendência</th>
                  </tr>
                </thead>
                <tbody>
                  {evolucao.gases.map((g) => (
                    <tr key={g.chave}>
                      <td>{g.rotulo}</td>
                      <td>
                        {ppm(g.anterior.valor)}
                        <div className="dga-sub">{dataBR(g.anterior.data)}</div>
                      </td>
                      <td>
                        <b>{ppm(g.atual.valor)}</b>
                        <div className="dga-sub">{dataBR(g.atual.data)}</div>
                      </td>
                      <td>{sinal(g.variacaoAbsoluta)}</td>
                      <td>
                        {g.variacaoPercentual !== null ? (
                          `${sinal(g.variacaoPercentual)}%`
                        ) : (
                          <span className="dga-nao-aplicavel">{g.observacao ?? nada}</span>
                        )}
                      </td>
                      <td>{g.meses ?? nada}</td>
                      <td>
                        {g.taxaMensalPercentual !== null ? `${sinal(g.taxaMensalPercentual)}%/mês` : nada}
                      </td>
                      <td>
                        <span className={`pill pill-${tomDaTendencia(g.tendencia)}`}>{g.tendencia}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Acumulado só aparece com três ou mais análises — com duas ele
                repetiria a linha de cima com outro nome. */}
            {evolucao.gases.some((g) => g.acumulado) && (
              <>
                <h5 className="dga-subtitulo">
                  Acumulado do período ({evolucao.analises} análises)
                </h5>
                <div className="dga-rolagem">
                  <table className="dga-tabela">
                    <thead>
                      <tr>
                        <th>Gás</th>
                        <th>Primeira</th>
                        <th>Última</th>
                        <th>Δ ppm</th>
                        <th>Δ % total</th>
                        <th>Taxa mensal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evolucao.gases
                        .filter((g) => g.acumulado)
                        .map((g) => (
                          <tr key={g.chave}>
                            <td>{g.rotulo}</td>
                            <td>{dataBR(g.acumulado!.primeiraData)}</td>
                            <td>{dataBR(g.acumulado!.ultimaData)}</td>
                            <td>{sinal(g.acumulado!.variacaoAbsoluta)}</td>
                            <td>
                              {g.acumulado!.variacaoPercentual !== null
                                ? `${sinal(g.acumulado!.variacaoPercentual)}%`
                                : nada}
                            </td>
                            <td>
                              {g.acumulado!.taxaMensalPercentual !== null
                                ? `${sinal(g.acumulado!.taxaMensalPercentual)}%/mês`
                                : nada}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <p className="dga-nota">{evolucao.convencao}</p>
          </>
        )}
      </div>
    </div>
  );
}
